import { useMemo, useState } from 'react'
import {
  AmountInput,
  Button,
  DatePicker,
  DateRangeChip,
  Drawer,
  SearchSelect,
  Segmented,
  SelectChip,
} from '../components'
import { useAccounts } from '../lib/api/accounts'
import { SALARY_DEFAULT_ORG_NAME, useDefaultOrg, useOrganizations } from '../lib/api/organizations'
import {
  flattenSalaryEntries,
  useAddSalaryPayment,
  useDeleteSalaryPayment,
  useSalaryEntries,
  useSalaryPayments,
  useSalaryReport,
  type SalaryReportMetric,
} from '../lib/api/salaries'
import { fmtMoney } from '../lib/money'
import { useMe } from '../lib/use-me'
import { canDelete } from '../lib/permissions'
import type { SalaryEntry } from '../lib/api/types'

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function firstDayOfMonth(year: number, month: number): string {
  return `${year}-${pad2(month)}-01`
}

function lastDayOfMonth(year: number, month: number): string {
  const day = new Date(year, month, 0).getDate()
  return `${year}-${pad2(month)}-${pad2(day)}`
}

function defaultRange(): { from: string; to: string } {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1 // 1-12
  // Last 6 months including current — first day of (current - 5) to last day of current.
  const fromMonthRaw = month - 5
  let fromYear = year
  let fromMonth = fromMonthRaw
  while (fromMonth <= 0) {
    fromMonth += 12
    fromYear -= 1
  }
  return {
    from: firstDayOfMonth(fromYear, fromMonth),
    to: lastDayOfMonth(year, month),
  }
}

function fmtCellValue(value: number): string {
  return value.toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const METRIC_OPTIONS: { value: SalaryReportMetric; label: string }[] = [
  { value: 'gross', label: 'Нараховано' },
  { value: 'paid', label: 'Виплачено' },
  { value: 'debt', label: 'Заборгованість' },
  { value: 'net', label: 'Нетто' },
]

type PaymentCell = {
  employeeId: string
  employeeName: string
  periodKey: string
}

export function ReportTab() {
  const initialRange = useMemo(() => defaultRange(), [])
  const [dateFrom, setDateFrom] = useState<string>(initialRange.from)
  const [dateTo, setDateTo] = useState<string>(initialRange.to)
  const [metric, setMetric] = useState<SalaryReportMetric>('debt')
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [paymentCell, setPaymentCell] = useState<PaymentCell | null>(null)

  const orgs = useOrganizations()
  useDefaultOrg(orgs.data, organizationId, setOrganizationId, SALARY_DEFAULT_ORG_NAME)

  const reportQ = useSalaryReport(
    { dateFrom, dateTo, metric, organizationId },
    { enabled: !!organizationId },
  )

  if (orgs.isLoading) {
    return <div style={{ padding: 24, color: 'var(--fg-3)' }}>Завантаження…</div>
  }

  if ((orgs.data ?? []).length === 0) {
    return (
      <div className="card" style={{ padding: 24, color: 'var(--fg-3)' }}>
        Спочатку створіть організацію
      </div>
    )
  }

  const showOrgSelect = (orgs.data ?? []).length > 1
  const periods = reportQ.data?.periods ?? []
  const rows = reportQ.data?.rows ?? []
  // Column totals: sum each period down its column, plus the grand total of the
  // per-row "Усього" — so every metric view (gross/paid/debt/net) gets a footer.
  const columnTotal = (period: string) =>
    rows.reduce((sum, row) => sum + (row.totals[period] ?? 0), 0)
  const grandTotal = rows.reduce((sum, row) => sum + row.total, 0)

  return (
    <div className="card" style={{ padding: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Період</span>
          <DateRangeChip
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => {
              setDateFrom(f)
              setDateTo(t)
            }}
          />
        </div>
        {showOrgSelect && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Організація</span>
            <SelectChip
              label="Організація"
              items={(orgs.data ?? []).map((o) => ({ id: o.id, name: o.name }))}
              selectedId={organizationId}
              onChange={(id) => setOrganizationId(id)}
            />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Метрика</span>
          <Segmented<SalaryReportMetric>
            options={METRIC_OPTIONS}
            value={metric}
            onChange={setMetric}
          />
        </div>
      </div>

      {reportQ.isLoading ? (
        <div style={{ padding: 24, color: 'var(--fg-3)' }}>Завантаження…</div>
      ) : reportQ.isError ? (
        <div style={{ padding: 24, color: 'var(--danger-fg)' }}>
          Помилка завантаження звіту
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--fg-3)' }}>Немає даних у діапазоні</div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '8px 12px',
                    fontWeight: 500,
                    color: 'var(--fg-3)',
                  }}
                >
                  ПІБ
                </th>
                {periods.map((p) => (
                  <th
                    key={p}
                    style={{
                      textAlign: 'right',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: 'var(--fg-3)',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p}
                  </th>
                ))}
                <th
                  style={{
                    textAlign: 'right',
                    padding: '8px 12px',
                    fontWeight: 500,
                    color: 'var(--fg-3)',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Усього
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.employeeId} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 12px' }}>{row.name}</td>
                  {periods.map((p) => {
                    const value = row.totals[p] ?? 0
                    const isDebtClickable = metric === 'debt' && value > 0
                    const baseCell: React.CSSProperties = {
                      padding: '6px 12px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }
                    if (value === 0) {
                      return (
                        <td key={p} style={{ ...baseCell, color: 'var(--fg-4)' }}>
                          —
                        </td>
                      )
                    }
                    if (isDebtClickable) {
                      return (
                        <td
                          key={p}
                          onClick={() =>
                            setPaymentCell({
                              employeeId: row.employeeId,
                              employeeName: row.name,
                              periodKey: p,
                            })
                          }
                          style={{
                            ...baseCell,
                            cursor: 'pointer',
                            color: 'var(--fg-1)',
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                          }}
                          onMouseEnter={(e) => {
                            ;(e.currentTarget as HTMLTableCellElement).style.background =
                              'var(--bg-2)'
                          }}
                          onMouseLeave={(e) => {
                            ;(e.currentTarget as HTMLTableCellElement).style.background = ''
                          }}
                        >
                          {fmtCellValue(value)}
                        </td>
                      )
                    }
                    return (
                      <td key={p} style={baseCell}>
                        {fmtCellValue(value)}
                      </td>
                    )
                  })}
                  <td
                    style={{
                      padding: '6px 12px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                    }}
                  >
                    {row.total === 0 ? (
                      <span style={{ color: 'var(--fg-4)', fontWeight: 400 }}>—</span>
                    ) : (
                      fmtCellValue(row.total)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-2)' }}>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--fg-2)' }}>
                  Усього
                </td>
                {periods.map((p) => {
                  const value = columnTotal(p)
                  return (
                    <td
                      key={p}
                      style={{
                        padding: '8px 12px',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                      }}
                    >
                      {value === 0 ? (
                        <span style={{ color: 'var(--fg-4)', fontWeight: 400 }}>—</span>
                      ) : (
                        fmtCellValue(value)
                      )}
                    </td>
                  )
                })}
                <td
                  style={{
                    padding: '8px 12px',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    fontWeight: 700,
                  }}
                >
                  {grandTotal === 0 ? (
                    <span style={{ color: 'var(--fg-4)', fontWeight: 400 }}>—</span>
                  ) : (
                    fmtCellValue(grandTotal)
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {paymentCell && (
        <PaymentsDrawer
          cell={paymentCell}
          organizationId={organizationId}
          onClose={() => setPaymentCell(null)}
        />
      )}
    </div>
  )
}

type PaymentsDrawerProps = {
  cell: PaymentCell
  organizationId: string | null
  onClose: () => void
}

function PaymentsDrawer({ cell, organizationId, onClose }: PaymentsDrawerProps) {
  const periodYear = Number(cell.periodKey.slice(0, 4))
  const periodMonth = Number(cell.periodKey.slice(5, 7))

  // Exactly one accrual per employee per period now, but render whatever the
  // period holds — each accrual carries its own payout history.
  const entriesQ = useSalaryEntries(
    { employeeId: cell.employeeId, periodYear, periodMonth },
    { enabled: true },
  )
  const entries = useMemo(() => flattenSalaryEntries(entriesQ.data), [entriesQ.data])

  return (
    <Drawer title="Виплати" onClose={onClose}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 12,
        }}
      >
        {cell.employeeName} · {cell.periodKey}
      </div>

      {entriesQ.isLoading ? (
        <div style={{ color: 'var(--fg-3)' }}>Завантаження…</div>
      ) : entriesQ.isError ? (
        <div style={{ color: 'var(--danger-fg)' }}>Помилка завантаження</div>
      ) : entries.length === 0 ? (
        <div style={{ color: 'var(--fg-3)' }}>Немає нарахувань за період</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {entries.map((entry) => (
            <AccrualPayments key={entry.id} entry={entry} organizationId={organizationId} />
          ))}
        </div>
      )}
    </Drawer>
  )
}

type AccrualPaymentsProps = {
  entry: SalaryEntry
  organizationId: string | null
}

// One accrual: its running totals, the payouts recorded against it, and a form
// to add another (an accrual can be settled across several payouts).
function AccrualPayments({ entry, organizationId }: AccrualPaymentsProps) {
  const me = useMe()
  const allowDelete = canDelete(me.data?.role)
  // Payouts may come from any account (employees aren't org-bound), so offer
  // every matching-currency account, not just the report's organization.
  const accountsQ = useAccounts({})
  const paymentsQ = useSalaryPayments(entry.id)
  const addPayment = useAddSalaryPayment()
  const deletePayment = useDeleteSalaryPayment()

  const debt = entry.debt
  const [accountId, setAccountId] = useState<string>('')
  const [amount, setAmount] = useState<string>(debt > 0 ? String(debt) : '')
  const [date, setDate] = useState<string>(entry.paymentDate)
  const [error, setError] = useState<string | null>(null)

  const payments = paymentsQ.data?.items ?? []
  const matchingAccounts = (accountsQ.data ?? []).filter(
    (a) => a.currency === entry.currency && !a.isArchived,
  )

  void organizationId // accounts intentionally span organizations

  const handleAdd = () => {
    const amt = Number(amount)
    if (!accountId || !Number.isFinite(amt) || amt <= 0) return
    setError(null)
    addPayment.mutate(
      { id: entry.id, accountId, amount: amt, paymentDate: date },
      {
        onSuccess: () => {
          setAmount('')
          setAccountId('')
        },
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : String(err))
        },
      },
    )
  }

  const handleDelete = (paymentId: string) => {
    deletePayment.mutate({ id: entry.id, paymentId })
  }

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
        <span style={{ color: 'var(--fg-3)' }}>Нараховано (нетто)</span>
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtMoney(entry.net, entry.currency)}
        </strong>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
        <span style={{ color: 'var(--fg-3)' }}>Виплачено</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtMoney(entry.paid, entry.currency)}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
        <span style={{ color: 'var(--fg-3)' }}>Залишок</span>
        <strong
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: debt > 0 ? 'var(--danger-fg)' : 'var(--fg-3)',
          }}
        >
          {fmtMoney(debt, entry.currency)}
        </strong>
      </div>

      {/* Existing payouts */}
      {payments.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {payments.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 12,
                color: 'var(--fg-2)',
                borderTop: '1px solid var(--border)',
                paddingTop: 4,
              }}
            >
              <span>{p.paymentDate}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(p.amount, entry.currency)}
              </span>
              {allowDelete ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="trash"
                  onClick={() => handleDelete(p.id)}
                  disabled={deletePayment.isPending}
                >
                  {''}
                </Button>
              ) : (
                <span style={{ width: 28 }} />
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Add a payout */}
      {debt > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: 'var(--fg-3)',
            }}
          >
            <span>Рахунок</span>
            <SearchSelect
              style={{ ...inputStyle, width: '100%' }}
              value={accountId}
              onChange={setAccountId}
              disabled={matchingAccounts.length === 0}
              emptyOption
              emptyLabel="— оберіть рахунок —"
              placeholder="— оберіть рахунок —"
              options={matchingAccounts.map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
            />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 12,
                color: 'var(--fg-3)',
                flex: 1,
              }}
            >
              <span>Сума</span>
              <AmountInput
                value={amount}
                onChange={setAmount}
                decimals={2}
                style={{ ...inputStyle, width: '100%' }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 12,
                color: 'var(--fg-3)',
                flex: 1,
              }}
            >
              <span>Дата</span>
              <DatePicker
                value={date}
                onChange={setDate}
                style={{ ...inputStyle, width: '100%' }}
              />
            </label>
          </div>
          {matchingAccounts.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
              Немає підходящих рахунків (потрібна валюта {entry.currency})
            </div>
          ) : null}
          <div>
            <Button
              variant="primary"
              size="sm"
              icon="check"
              onClick={handleAdd}
              disabled={
                !accountId ||
                !(Number(amount) > 0) ||
                matchingAccounts.length === 0 ||
                addPayment.isPending
              }
            >
              {addPayment.isPending ? 'Збереження…' : 'Провести виплату'}
            </Button>
          </div>
          {error ? (
            <div style={{ fontSize: 12, color: 'var(--danger-fg)' }}>Помилка: {error}</div>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>Нарахування повністю виплачено</div>
      )}
    </div>
  )
}
